import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCw, Send } from 'lucide-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ContractSummaryModal } from '../../components/Suspensions/contract-summary-modal';
import { SuspensionsDueTodayList } from './SuspensionsDueTodayList';
import {
  SuspensionMarkDialog,
  type SuspensionMarkPayload,
} from '../../components/Suspensions/suspension-mark-dialog';
import contractLineService, {
  SuspensionAutomationHistoryItem,
  SuspensionAutomationHistoryQuery,
  SuspensionsDueTodayLine,
  SuspensionsDueTodayResponse,
} from '../../services/contracts/ContractLineService';
import { useToast } from '../../hooks/useToast';
import { extractApiErrorMessage, isCanceledError } from '../../services/utils/apiErrorUtils';

type DueTodayCardFilter =
  | 'all'
  | 'first'
  | 'impedimento'
  | 'missingCred'
  | 'queued'
  | 'eligible'
  | 'completed'
  | 'failed';

const isLineNotFoundAutomationError = (category?: string | null): boolean => {
  return category === 'linha_nao_encontrada';
};

const SuspensionsDueTodayPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [data, setData] = useState<SuspensionsDueTodayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [enqueueing, setEnqueueing] = useState(false);
  const [retryingBatchId, setRetryingBatchId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [contractSummaryModalOpen, setContractSummaryModalOpen] = useState(false);
  const [selectedContractForSummary, setSelectedContractForSummary] = useState<string | null>(null);
  const [contractSummaryReloadKey, setContractSummaryReloadKey] = useState(0);
  const [markDialogOpen, setMarkDialogOpen] = useState(false);
  const [pendingMarkLineIds, setPendingMarkLineIds] = useState<string[]>([]);
  const [cardFilter, setCardFilter] = useState<DueTodayCardFilter>('all');
  const [clearingQueue, setClearingQueue] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<SuspensionAutomationHistoryItem[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyFilters, setHistoryFilters] = useState<SuspensionAutomationHistoryQuery>({
    page: 1,
    pageSize: 50,
    dateFrom: '',
    dateTo: '',
    razaoSocial: '',
    cnpj: '',
    protocolo: '',
    linha: '',
  });

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const res = await contractLineService.getSuspensionsDueToday({ signal });
      if (signal?.aborted) return;
      setData(res);
      setSelected(new Set());
    } catch (e: unknown) {
      if (isCanceledError(e) || signal?.aborted) return;
      console.error(e);
      showError('Erro ao carregar suspensões do dia');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [showError]);

  const loadHistory = useCallback(async (
    next?: Partial<SuspensionAutomationHistoryQuery>,
    signal?: AbortSignal
  ) => {
    const merged = {
      ...historyFilters,
      ...next,
    };
    setHistoryFilters(merged);
    setHistoryLoading(true);
    try {
      const response = await contractLineService.getSuspensionAutomationHistory(merged, { signal });
      if (signal?.aborted) return;
      setHistory(response.items);
      setHistoryTotal(response.total);
    } catch (e: unknown) {
      if (isCanceledError(e) || signal?.aborted) return;
      console.error(e);
      showError('Erro ao carregar histórico de suspensões.');
    } finally {
      if (!signal?.aborted) setHistoryLoading(false);
    }
  }, [historyFilters, showError]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  useEffect(() => {
    const ac = new AbortController();
    void loadHistory({ page: 1 }, ac.signal);
    return () => ac.abort();
  }, []);

  const enqueueableLines = useMemo(() => {
    if (!data) return [];
    return data.items.flatMap((c) => c.lines.filter((l) => l.canEnqueue));
  }, [data]);

  const toggleLine = (lineId: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(lineId);
      else next.delete(lineId);
      return next;
    });
  };

  const selectAllEnqueueable = () => {
    setSelected(new Set(enqueueableLines.map((l) => l.lineId)));
  };

  const clearSelection = () => setSelected(new Set());

  const handleViewContractSummary = (contractId: string) => {
    setSelectedContractForSummary(contractId);
    setContractSummaryModalOpen(true);
  };

  const handleCredentialsUpdated = () => {
    void load();
    setContractSummaryReloadKey((k) => k + 1);
  };

  const handleMarkAsSuspended = (lineId: string) => {
    setPendingMarkLineIds([lineId]);
    setMarkDialogOpen(true);
  };

  const handleRequestBatchSuspend = (lineIds: string[]) => {
    setPendingMarkLineIds(lineIds);
    setMarkDialogOpen(true);
  };

  const handleConfirmMarkSuspended = async (payload: SuspensionMarkPayload) => {
    const suspensionDate =
      payload.mode === 'retroactive' && payload.suspensionDate
        ? new Date(`${payload.suspensionDate}T12:00:00`).toISOString()
        : undefined;
    const digits = payload.protocolo?.replace(/\D/g, '') ?? '';
    try {
      const result = await contractLineService.markAsSuspendedBatch({
        lineIds: pendingMarkLineIds,
        suspensionDate,
        ...(digits.length >= 10 ? { protocolo: digits } : {}),
      });
      showSuccess(`${result.updated} linha(s) marcada(s) como suspensa(s).`);
      await load();
      if (contractSummaryModalOpen) {
        setContractSummaryReloadKey((k) => k + 1);
      }
    } catch (error: unknown) {
      console.error('Erro ao marcar como suspensa:', error);
      showError(extractApiErrorMessage(error, 'Erro ao marcar linha(s) como suspensa'));
      // Relança para o diálogo não fechar. O diálogo captura e não propaga ao Sentry.
      throw error;
    }
  };

  const handleUnmarkSuspended = async (lineId: string) => {
    try {
      await contractLineService.unmarkAsSuspended(lineId);
      showSuccess('Suspensão revertida com sucesso!');
      await load();
      if (contractSummaryModalOpen) {
        setContractSummaryReloadKey((k) => k + 1);
      }
    } catch (error: unknown) {
      console.error('Erro ao reverter suspensão:', error);
      const err = error as { response?: { data?: { message?: string } } };
      showError(err.response?.data?.message || 'Erro ao reverter suspensão');
    }
  };

  const handleRetryBatch = async (batchId: string) => {
    setRetryingBatchId(batchId);
    try {
      await contractLineService.retrySuspensionAutomationBatch(batchId);
      showSuccess('Lote reenfileirado para nova tentativa.');
      await load();
    } catch (e: unknown) {
      console.error(e);
      showError('Não foi possível reenfileirar o lote.');
    } finally {
      setRetryingBatchId(null);
    }
  };

  const handleEnqueue = async () => {
    if (selected.size === 0) {
      showError('Selecione ao menos uma linha elegível.');
      return;
    }
    setEnqueueing(true);
    try {
      const res = await contractLineService.enqueueSuspensionAutomation(Array.from(selected));
      const batchCount = res.batches?.length ?? 0;
      showSuccess(
        batchCount > 0
          ? `${res.enqueued} linha(s) em ${batchCount} lote(s) de automação.`
          : `${res.enqueued} linha(s) enfileirada(s) para automação.`
      );
      if (res.skipped > 0) {
        showError(`${res.skipped} linha(s) ignorada(s) — veja detalhes no resultado.`);
      }
      await load();
    } catch (e: unknown) {
      console.error(e);
      showError('Erro ao enfileirar para automação');
    } finally {
      setEnqueueing(false);
    }
  };

  const handleEnqueueLine = async (lineId: string) => {
    setEnqueueing(true);
    try {
      const res = await contractLineService.enqueueSuspensionAutomation([lineId]);
      showSuccess(
        res.enqueued > 0
          ? 'Linha enviada para a automação de suspensão.'
          : 'A automação não enfileirou esta linha.'
      );
      await load();
    } catch (e: unknown) {
      console.error(e);
      showError('Erro ao enfileirar para automação');
    } finally {
      setEnqueueing(false);
    }
  };

  const handleClearQueue = async () => {
    if (!data?.items.length) {
      showError('Não há lotes para limpar nesta visualização.');
      return;
    }

    const ok = window.confirm(
      'Tem certeza que deseja limpar os lotes desta tela? Isso força finalização operacional (qualquer status).'
    );
    if (!ok) return;

    setClearingQueue(true);
    try {
      const batchIds = data.items
        .map((item) => item.batchId)
        .filter((id): id is string => Boolean(id));
      const result = await contractLineService.clearSuspensionAutomationQueue({
        reason: 'limpeza_operacional_due_today',
        batchIds,
      });
      showSuccess(
        `Fila limpa. Lotes: ${result.batchesUpdated} · Jobs: ${result.jobsUpdated}`
      );
      await load();
      await loadHistory({ page: 1 });
    } catch (e: unknown) {
      console.error(e);
      showError('Erro ao limpar fila de automação.');
    } finally {
      setClearingQueue(false);
    }
  };

  const filteredContracts = useMemo(() => {
    if (!data) return [];
    if (cardFilter === 'all') return data.items;

    const lineMatches = (line: SuspensionsDueTodayLine): boolean => {
      switch (cardFilter) {
        case 'first':
          return line.kind === 'first_suspension';
        case 'impedimento':
          return line.hasImpedimento;
        case 'missingCred':
          return !line.temCredenciais;
        case 'queued':
          return line.automationStatus === 'pending' || line.automationStatus === 'processing';
        case 'eligible':
          return line.canEnqueue;
        case 'completed':
          return line.automationStatus === 'done';
        case 'failed':
          return line.automationStatus === 'failed';
        default:
          return true;
      }
    };

    return data.items
      .map((contract) => ({
        ...contract,
        lines: contract.lines.filter(lineMatches),
      }))
      .filter((contract) => contract.lines.length > 0);
  }, [data, cardFilter]);

  const lineNotFoundCount = useMemo(() => {
    if (!data) return 0;
    return data.items.reduce((acc, contract) => {
      const count = contract.lines.filter(
        (line) => line.automationStatus === 'failed' && isLineNotFoundAutomationError(line.automationErrorCategory)
      ).length;
      return acc + count;
    }, 0);
  }, [data]);


  return (
    <GlassPageWrapper title="Contratos a suspender hoje" icon="robot">
      <div className="space-y-4">
        <Alert>
          <AlertDescription>
            Uma lista das linhas Fastchip a suspender hoje (calendário SP, ciclo 120 dias).
            Confira cliente, contrato, linha e o e-mail do Meu TIM, depois chame a automação.
            O botão <strong>Abrir ticket</strong> só aparece em caso crítico e não cria protocolo sozinho.{' '}
            <Link to="/suspensions" className="underline font-medium">
              Abrir dashboard completo
            </Link>
          </AlertDescription>
        </Alert>
        {lineNotFoundCount > 0 && (
          <Alert className="border-amber-300 bg-amber-50">
            <AlertDescription className="text-amber-800">
              {lineNotFoundCount} linha(s) com retorno TIM "não encontrada". Revise no portal: pode já
              estar suspensa ou com número divergente.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Atualizar
          </Button>
          <Button variant="outline" size="sm" onClick={selectAllEnqueueable} disabled={loading || enqueueableLines.length === 0}>
            Selecionar elegíveis ({enqueueableLines.length})
          </Button>
          <Button variant="outline" size="sm" onClick={clearSelection} disabled={selected.size === 0}>
            Limpar seleção
          </Button>
          <Button variant="outline" size="sm" onClick={handleClearQueue} disabled={clearingQueue || !data?.items.length}>
            {clearingQueue ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Limpar fila
          </Button>
          <Button size="sm" onClick={handleEnqueue} disabled={enqueueing || selected.size === 0}>
            {enqueueing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            Enviar para automação ({selected.size})
          </Button>
          {enqueueableLines.length > 0 && (
            <span className={`text-sm font-medium px-2 py-1 rounded border ${
              selected.size > 0
                ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                : 'text-muted-foreground bg-white/60 border-border'
            }`}>
              {selected.size} de {enqueueableLines.length} selecionado{selected.size !== 1 ? 's' : ''}
            </span>
          )}
          {data && (
            <span className="text-sm text-muted-foreground ml-2">
              Referência: {data.referenceDateSp}
            </span>
          )}

        <div className="rounded-lg border bg-white/70 p-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground">Data inicial</label>
              <input
                type="date"
                className="border rounded px-2 py-1 text-sm"
                value={historyFilters.dateFrom ?? ''}
                onChange={(e) => setHistoryFilters((p) => ({ ...p, dateFrom: e.target.value, page: 1 }))}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-muted-foreground">Data final</label>
              <input
                type="date"
                className="border rounded px-2 py-1 text-sm"
                value={historyFilters.dateTo ?? ''}
                onChange={(e) => setHistoryFilters((p) => ({ ...p, dateTo: e.target.value, page: 1 }))}
              />
            </div>
            <input
              className="border rounded px-2 py-1 text-sm"
              placeholder="Razão social"
              value={historyFilters.razaoSocial ?? ''}
              onChange={(e) => setHistoryFilters((p) => ({ ...p, razaoSocial: e.target.value, page: 1 }))}
            />
            <input
              className="border rounded px-2 py-1 text-sm"
              placeholder="CNPJ"
              value={historyFilters.cnpj ?? ''}
              onChange={(e) => setHistoryFilters((p) => ({ ...p, cnpj: e.target.value, page: 1 }))}
            />
            <input
              className="border rounded px-2 py-1 text-sm"
              placeholder="Protocolo"
              value={historyFilters.protocolo ?? ''}
              onChange={(e) => setHistoryFilters((p) => ({ ...p, protocolo: e.target.value, page: 1 }))}
            />
            <input
              className="border rounded px-2 py-1 text-sm"
              placeholder="Linha"
              value={historyFilters.linha ?? ''}
              onChange={(e) => setHistoryFilters((p) => ({ ...p, linha: e.target.value, page: 1 }))}
            />
            <Button size="sm" variant="outline" onClick={() => void loadHistory({ page: 1 })} disabled={historyLoading}>
              {historyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Buscar histórico
            </Button>
          </div>

          <div className="text-sm text-muted-foreground">
            Histórico: {historyTotal} registro(s)
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-3">Data</th>
                  <th className="py-2 pr-3">Cliente</th>
                  <th className="py-2 pr-3">Linha</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Protocolo</th>
                  <th className="py-2 pr-3">Erro</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.jobId} className="border-b last:border-b-0">
                    <td className="py-2 pr-3">{new Date(item.queuedAt).toLocaleString('pt-BR')}</td>
                    <td className="py-2 pr-3">{item.clientName} ({item.clientCnpj})</td>
                    <td className="py-2 pr-3">({item.ddd}) {item.numero ?? '—'}</td>
                    <td className="py-2 pr-3">{item.status}</td>
                    <td className="py-2 pr-3">{item.protocolo ?? '-'}</td>
                    <td className="py-2 pr-3">{item.errorMessage ?? '-'}</td>
                  </tr>
                ))}
                {history.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-3 text-muted-foreground">Nenhum histórico encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 text-center text-xs">
            <Stat label="Total" value={data.summary.totalLines} active={cardFilter === 'all'} onClick={() => setCardFilter('all')} />
            <Stat label="1ª susp." value={data.summary.firstSuspension} active={cardFilter === 'first'} onClick={() => setCardFilter('first')} />
            <Stat label="Impedimento" value={data.summary.withImpedimento} active={cardFilter === 'impedimento'} onClick={() => setCardFilter('impedimento')} />
            <Stat label="Sem cred." value={data.summary.missingCredentials} active={cardFilter === 'missingCred'} onClick={() => setCardFilter('missingCred')} />
            <Stat label="Na fila" value={data.summary.alreadyQueued} active={cardFilter === 'queued'} onClick={() => setCardFilter('queued')} />
            <Stat label="Concluídas" value={data.summary.completed ?? 0} active={cardFilter === 'completed'} onClick={() => setCardFilter('completed')} />
            <Stat label="Erros" value={data.summary.failed ?? 0} active={cardFilter === 'failed'} onClick={() => setCardFilter('failed')} />
            <Stat label="Elegíveis" value={data.summary.canEnqueue} active={cardFilter === 'eligible'} onClick={() => setCardFilter('eligible')} />
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && data && filteredContracts.length === 0 && (
          <p className="text-center text-muted-foreground py-8">Nenhuma linha para suspender hoje.</p>
        )}

        {!loading && filteredContracts.length > 0 && (
          <SuspensionsDueTodayList
            contracts={filteredContracts}
            selected={selected}
            enqueueing={enqueueing}
            onToggleLine={toggleLine}
            onEnqueueLine={(lineId) => void handleEnqueueLine(lineId)}
            onRetryBatch={(batchId) => void handleRetryBatch(batchId)}
            retryingBatchId={retryingBatchId}
            onOpenDetails={handleViewContractSummary}
            onCredentialsSaved={() => void load()}
          />
        )}
      </div>

      <ContractSummaryModal
        contractId={selectedContractForSummary}
        open={contractSummaryModalOpen}
        onClose={() => {
          setContractSummaryModalOpen(false);
          setSelectedContractForSummary(null);
        }}
        onCredentialsUpdated={handleCredentialsUpdated}
        onMarkSuspended={handleMarkAsSuspended}
        onRequestBatchSuspend={handleRequestBatchSuspend}
        onUnmarkSuspended={handleUnmarkSuspended}
        onBatchUpdated={load}
        reloadKey={contractSummaryReloadKey}
      />

      <SuspensionMarkDialog
        open={markDialogOpen}
        lineCount={pendingMarkLineIds.length}
        onClose={() => {
          setMarkDialogOpen(false);
          setPendingMarkLineIds([]);
        }}
        onConfirm={handleConfirmMarkSuspended}
      />
    </GlassPageWrapper>
  );
};

function Stat({
  label,
  value,
  active,
  onClick,
}: {
  label: string;
  value: number;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2 py-2 w-full transition ${
        active ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white/70 hover:bg-muted/40'
      }`}
    >
      <div className="font-bold text-lg">{value}</div>
      <div className="text-muted-foreground">{label}</div>
    </button>
  );
}

export default SuspensionsDueTodayPage;
